import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Bell, Sparkles, EyeOff, Megaphone, Inbox, Tag } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { formatModelLabel } from "@/utils/formatModelLabel";

export function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "";
  }
}

/**
 * Auto-generated entries (model shown/hidden/price_changed) carry a stable
 * `meta.action`/`meta.modelId`, not a final display string — the text is resolved here via i18n
 * so it stays in sync with the site's language. `item.title` is only an English fallback (used by
 * the non-localized admin UI).
 */
export function systemNotificationTitle(item, t) {
  if (item.source === "auto_model_catalog" && item.meta?.action && item.meta?.modelId) {
    const modelId = formatModelLabel(item.meta.modelId);
    if (item.meta.action === "price_changed") {
      return item.meta.price === null || item.meta.price === undefined
        ? t("notif_auto_model_price_cleared", { modelId })
        : t("notif_auto_model_price_updated", { modelId, price: item.meta.price });
    }
    const key = item.meta.action === "shown" ? "notif_auto_model_shown" : "notif_auto_model_hidden";
    return t(key, { modelId });
  }
  return item.title;
}

export function notificationVariant(item) {
  if (item.source === "auto_model_catalog") {
    if (item.meta?.action === "price_changed") return "price";
    return item.meta?.action === "hidden" ? "hidden" : "shown";
  }
  return "manual";
}

export const VARIANT_ICON = {
  shown: Sparkles,
  hidden: EyeOff,
  price: Tag,
  manual: Megaphone,
};

export default function NotificationsModal({
  open,
  onClose,
  systemNotifications,
  markSeen,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) {
  const { t } = useLang();

  useEffect(() => {
    if (open) markSeen();
  }, [open, markSeen]);

  if (!open) return null;

  // Portaled: `.app-main` is a stacking context below the sidebar, so an in-tree overlay would sit under it.
  return createPortal(
    <div
      className="auth-overlay notifications-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="auth-modal notifications-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notifications-modal-title"
      >
        <div className="notifications-modal-header">
          <span className="notifications-modal-icon" aria-hidden="true">
            <Bell size={16} />
          </span>
          <h2 id="notifications-modal-title" className="notifications-modal-title">
            {t("notif_modal_title")}
          </h2>
          <button className="auth-close notifications-modal-close" onClick={onClose} aria-label={t("common_close")}>
            <X size={18} />
          </button>
        </div>

        {systemNotifications.length === 0 ? (
          <div className="notifications-empty">
            <span className="notifications-empty-icon" aria-hidden="true">
              <Inbox size={26} />
            </span>
            <p className="notifications-empty-text">{t("notif_empty_system")}</p>
          </div>
        ) : (
          <div className="notifications-list">
            {systemNotifications.map((item, index) => {
              const variant = notificationVariant(item);
              const Icon = VARIANT_ICON[variant];
              return (
                <article
                  key={item._id}
                  className={`notifications-item notifications-item--${variant}`}
                  style={{ "--notif-delay": `${Math.min(index, 8) * 45}ms` }}
                >
                  <span className="notifications-item-rail" aria-hidden="true">
                    <span className="notifications-item-dot">
                      <Icon size={12} />
                    </span>
                    <span className="notifications-item-line" />
                  </span>
                  <div className="notifications-item-body">
                    <h3 className="notifications-item-title">{systemNotificationTitle(item, t)}</h3>
                    <span className="notifications-item-meta">{formatDate(item.createdAt)}</span>
                    {item.source !== "auto_model_catalog" && item.message ? (
                      <p className="notifications-item-message">{item.message}</p>
                    ) : null}
                  </div>
                </article>
              );
            })}
            {hasMore && (
              <button
                type="button"
                className="btn-load-more-models notifications-load-more"
                onClick={onLoadMore}
                disabled={loadingMore}
                data-testid="notifications-load-more"
              >
                {t("models_load_more")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
